import { addDays, format, isAfter, isBefore, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import type { BudgetPeriod } from '@/types/financial';

const ACTIVE_STEP_STORAGE_KEY = 'librattum-first-access-active-step';
const STEP_CHANGE_EVENT = 'librattum:first-access-step-change';
const STEP_COMMAND_EVENT = 'librattum:first-access-step-command';
const TUTORIAL_LAUNCH_EVENT = 'librattum:first-access-launch';

export const FIRST_ACCESS_TUTORIAL_MARKER = '[librattum-first-access]';

export const FIRST_ACCESS_TUTORIAL_STEPS = {
  dashboardNav: 'explain-dashboard-nav',
  dashboardPeriodCard: 'explain-dashboard-period-card',
  dashboardPeriodArrows: 'explain-dashboard-period-arrows',
  reportsNav: 'nav-reports',
  myAccountNav: 'nav-my-account',
  agentNav: 'nav-agents',
  agentCreate: 'create-agent',
  agentSave: 'submit-agent',
  budgetPeriodNav: 'nav-budget-period',
  mentoriaNav: 'nav-mentoria',
  supportNav: 'nav-support',
  helpCenterNav: 'nav-help-center',
  revenueCard: 'summary-revenue',
  revenueCreate: 'create-revenue',
  revenueSave: 'submit-revenue',
  revenueReceive: 'receive-revenue',
  revenueConfirm: 'confirm-revenue',
  futureCard: 'summary-future',
  futureCreate: 'create-future-expense',
  futureSave: 'submit-future-expense',
  futureInvest: 'invest-future-expense',
  futureConfirm: 'confirm-future-expense',
  monthlyCard: 'summary-monthly',
  monthlyCreate: 'create-monthly-expense',
  monthlySave: 'submit-monthly-expense',
  monthlyPay: 'pay-monthly-expense',
  monthlyConfirm: 'confirm-monthly-expense',
  dailyCard: 'summary-daily',
  dailyCreate: 'create-daily-expense',
  dailySave: 'submit-daily-expense',
  dailyLaunch: 'launch-daily-expense',
  dailyConfirm: 'confirm-daily-expense',
  balanceRevenue: 'explain-balance-revenue',
  balanceFuture: 'explain-balance-future',
  balanceMonthly: 'explain-balance-monthly',
  balanceDaily: 'explain-balance-daily',
  balanceResult: 'explain-balance-result',
  balanceVariation: 'explain-balance-variation',
} as const;

/**
 * Valores sugeridos no tutorial. Receitas e despesas passaram a ser lançamentos
 * REAIS do usuário: o nome é escolhido por ele na listagem existente e nenhuma
 * observação de tutorial é gravada, para não sujar um dado verdadeiro. Os
 * valores abaixo entram apenas como sugestão editável.
 */
export const FIRST_ACCESS_TUTORIAL_DEMO = {
  revenue: {
    amount: 350,
    receiptMethod: 'Cliente de teste',
  },
  futureExpense: {
    targetAmount: 600,
    presentValue: 0,
    investmentLocation: 'Investimento de teste',
  },
  monthlyExpense: {
    amount: 180,
  },
  dailyExpense: {
    frequency: 4,
    averageValue: 25,
  },
  agent: {
    name: 'Agente de teste',
    type: 'person' as const,
    description: `Agente criado no tutorial ${FIRST_ACCESS_TUTORIAL_MARKER}`,
  },
} as const;

type TutorialCommand = {
  type: 'advance';
  stepId?: string;
};

export type TutorialLaunchMode = 'welcome' | 'tour';

function isBrowser() {
  return typeof window !== 'undefined';
}

export function getActiveOnboardingStep(): string | null {
  if (!isBrowser()) return null;
  return window.sessionStorage.getItem(ACTIVE_STEP_STORAGE_KEY);
}

export function setActiveOnboardingStep(stepId: string | null) {
  if (!isBrowser()) return;

  if (stepId) {
    window.sessionStorage.setItem(ACTIVE_STEP_STORAGE_KEY, stepId);
  } else {
    window.sessionStorage.removeItem(ACTIVE_STEP_STORAGE_KEY);
  }

  window.dispatchEvent(
    new CustomEvent<string | null>(STEP_CHANGE_EVENT, {
      detail: stepId,
    })
  );
}

export function requestTutorialAdvance(stepId?: string) {
  if (!isBrowser()) return;

  window.dispatchEvent(
    new CustomEvent<TutorialCommand>(STEP_COMMAND_EVENT, {
      detail: {
        type: 'advance',
        stepId,
      },
    })
  );
}

export function requestFirstAccessTutorialLaunch(mode: TutorialLaunchMode = 'welcome') {
  if (!isBrowser()) return;

  window.dispatchEvent(
    new CustomEvent<TutorialLaunchMode>(TUTORIAL_LAUNCH_EVENT, {
      detail: mode,
    })
  );
}

export function subscribeToActiveOnboardingStep(listener: (stepId: string | null) => void) {
  if (!isBrowser()) return () => undefined;

  const handleChange = (event: Event) => {
    const customEvent = event as CustomEvent<string | null>;
    listener(customEvent.detail ?? getActiveOnboardingStep());
  };

  window.addEventListener(STEP_CHANGE_EVENT, handleChange);

  return () => {
    window.removeEventListener(STEP_CHANGE_EVENT, handleChange);
  };
}

export function subscribeToTutorialCommands(listener: (command: TutorialCommand) => void) {
  if (!isBrowser()) return () => undefined;

  const handleCommand = (event: Event) => {
    const customEvent = event as CustomEvent<TutorialCommand>;
    if (!customEvent.detail) return;
    listener(customEvent.detail);
  };

  window.addEventListener(STEP_COMMAND_EVENT, handleCommand);

  return () => {
    window.removeEventListener(STEP_COMMAND_EVENT, handleCommand);
  };
}

export function subscribeToFirstAccessTutorialLaunch(listener: (mode: TutorialLaunchMode) => void) {
  if (!isBrowser()) return () => undefined;

  const handleLaunch = (event: Event) => {
    const customEvent = event as CustomEvent<TutorialLaunchMode>;
    listener(customEvent.detail ?? 'welcome');
  };

  window.addEventListener(TUTORIAL_LAUNCH_EVENT, handleLaunch);

  return () => {
    window.removeEventListener(TUTORIAL_LAUNCH_EVENT, handleLaunch);
  };
}

export function useActiveOnboardingStep() {
  const [activeStep, setActiveStep] = useState<string | null>(() => getActiveOnboardingStep());

  useEffect(() => subscribeToActiveOnboardingStep(setActiveStep), []);

  return activeStep;
}

export function getTutorialDateInBudgetPeriod(
  period?: Pick<BudgetPeriod, 'start_date' | 'end_date'> | null
) {
  const today = new Date();

  if (!period?.start_date || !period?.end_date) {
    return format(today, 'yyyy-MM-dd');
  }

  const startDate = parseISO(period.start_date);
  const endDate = parseISO(period.end_date);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return format(today, 'yyyy-MM-dd');
  }

  if (isBefore(today, startDate)) {
    return format(startDate, 'yyyy-MM-dd');
  }

  if (isAfter(today, endDate)) {
    return format(endDate, 'yyyy-MM-dd');
  }

  return format(today, 'yyyy-MM-dd');
}

export function getTutorialFutureDate(
  period?: Pick<BudgetPeriod, 'end_date'> | null
) {
  if (!period?.end_date) {
    return format(addDays(new Date(), 15), 'yyyy-MM-dd');
  }

  const endDate = parseISO(period.end_date);
  if (Number.isNaN(endDate.getTime())) {
    return format(addDays(new Date(), 15), 'yyyy-MM-dd');
  }

  return format(endDate, 'yyyy-MM-dd');
}

const KNOWN_ITEM_IDS_STORAGE_KEY = 'librattum-first-access-known-item-ids';

/**
 * Guarda os ids já existentes antes de o usuário salvar o lançamento do
 * tutorial. Como o lançamento agora é real (sem nome nem marcador fixos), é
 * essa fotografia que permite identificar depois qual registro ele acabou de
 * criar — sem depender do relógio do cliente, que pode divergir do servidor.
 */
export function markTutorialKnownItemIds(scope: string, ids: readonly string[]) {
  if (!isBrowser()) return;

  const snapshot = readKnownItemIds();
  snapshot[scope] = [...ids];
  window.sessionStorage.setItem(KNOWN_ITEM_IDS_STORAGE_KEY, JSON.stringify(snapshot));
}

function readKnownItemIds(): Record<string, string[]> {
  if (!isBrowser()) return {};

  const raw = window.sessionStorage.getItem(KNOWN_ITEM_IDS_STORAGE_KEY);
  if (!raw) return {};

  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string[]>) : {};
  } catch {
    // Snapshot corrompido não pode derrubar o tutorial: recomeça vazio.
    return {};
  }
}

/**
 * Retorna o registro que o usuário criou durante o tutorial: aquele cujo id não
 * existia na fotografia tirada antes do salvamento. Havendo mais de um, vence o
 * mais recente. Sem fotografia (sessão nova), devolve `null` para não apontar
 * um lançamento antigo por engano.
 */
export function findTutorialCreatedItem<T extends { id: string; created_at?: string }>(
  scope: string,
  items: readonly T[] | null | undefined
): T | null {
  if (!items?.length) return null;

  const snapshot = readKnownItemIds()[scope];
  if (!snapshot) return null;

  const knownIds = new Set(snapshot);
  const createdItems = items.filter((item) => !knownIds.has(item.id));

  return createdItems.reduce<T | null>((latestItem, item) => {
    if (!latestItem) return item;

    const itemTime = new Date(item.created_at ?? 0).getTime();
    const latestTime = new Date(latestItem.created_at ?? 0).getTime();

    return itemTime > latestTime ? item : latestItem;
  }, null);
}
